import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

/**
 * Six ways a button can look, and they mean something:
 *
 * - `primary`: the one thing this screen is for. One per screen, in the accent colour.
 * - `secondary`: a real action that is not the main one (Cambiar, Actualizar, Descargar).
 * - `ghost`: navigation and small things inside a row (Editar, Quitar, subir, bajar).
 * - `danger`: something that cannot be undone. Only after a confirmation.
 * - `ai` and `ai-soft`: what is asked of the AI or taken from it, in its own blue. `ai`
 *   is the main button of the AI's sheet («Redactar», «Usar esta versión»); `ai-soft`
 *   opens it from a form («Ayuda a redactar con IA», the reviews). Nowhere else: blue
 *   says «this is the AI's».
 *
 * Three heights, and they line up with the rest: `sm` (32 px) sits inside a table row,
 * `md` (36 px) is the same height as a `.field`, so a filter and its button stay level,
 * `lg` (44 px) is the touch target for the one action that matters on a phone.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'ai' | 'ai-soft';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent text-white shadow-sm hover:bg-accent-2 focus-visible:outline-accent disabled:hover:bg-accent',
  secondary:
    'bg-card text-ink ring-1 ring-inset ring-line hover:bg-paper-2 focus-visible:outline-accent',
  ghost: 'text-ink-2 hover:bg-paper-2 hover:text-ink focus-visible:outline-accent',
  danger:
    'bg-danger-soft text-danger ring-1 ring-inset ring-danger/20 hover:bg-danger hover:text-white focus-visible:outline-danger',
  ai: 'bg-ai text-white shadow-sm hover:bg-ai-2 focus-visible:outline-ai disabled:hover:bg-ai',
  'ai-soft':
    'bg-ai-soft text-ai ring-1 ring-inset ring-ai/20 hover:bg-ai/15 hover:text-ai-2 focus-visible:outline-ai disabled:hover:bg-ai-soft disabled:hover:text-ai',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-2.5 text-[13px] gap-1.5',
  md: 'h-9 px-3.5 text-[14px] gap-2',
  lg: 'h-11 px-5 text-[15px] gap-2',
};

/** The same sizes as a floor, for a label that may take two lines (`wrap`). */
const WRAP_SIZES: Record<Size, string> = {
  sm: 'min-h-8 py-1 px-2.5 text-[13px] gap-1.5',
  md: 'min-h-9 py-1.5 px-3.5 text-[14px] gap-2',
  lg: 'min-h-11 py-2 px-5 text-[15px] gap-2',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** An icon drawn before the label. Decorative: the label is what says what it does. */
  icon?: ReactNode;
  /**
   * Let the label wrap instead of staying on one line: for a label built from data that
   * can be long («Correcto: reenviar a paulo@ferreteriacubas.com y copiar a …»). The
   * height becomes a floor and an address breaks anywhere rather than run out of its
   * card. A `className` cannot do this: `cn` joins classes without merging them, so a
   * `whitespace-normal` next to the base `whitespace-nowrap` loses to CSS order.
   */
  wrap?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  wrap = false,
  className,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md font-medium',
        wrap ? 'max-w-full min-w-0 whitespace-normal' : 'whitespace-nowrap',
        'transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        wrap ? WRAP_SIZES[size] : SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {icon ? <span className={cn('-ml-0.5 inline-flex', wrap && 'shrink-0')}>{icon}</span> : null}
      {wrap ? (
        <span className="min-w-0 text-left [overflow-wrap:anywhere]">{children}</span>
      ) : (
        children
      )}
    </button>
  );
}
